import * as swcHelpers from '@swc/helpers';
import React__default from 'react';
import { BloomreachApi, parsePageModel, CmsContainerPage, BloomreachProvider } from '@grandvisionhq/bloomreach';
import { LocaleUtils } from '@grandvisionhq/utils';

/**
 * Sets up the BloomreachProvider for any page and makes sure a pageModel is supplied.
 * This currently relies on our express servers to set the pageModel on the `res.locals` object.
 *
 * wrapInContainerPage: boolean Some pages are not container pages, like content and error pages
 *
 * @usage
 * First re-export from the opco site
 * const settings = {...}
 * export const withCms = withBloomreach(settings)
 *
 * const Page = () => {}
 * export default withCms()(Page)
 *
 * For non container pages
 * const Page = () => {}
 * export default withCms({ wrapInContainerPage: false })(Page)
 */ function withBloomreach(settings) {
    return ({ wrapInContainerPage  } = {
        wrapInContainerPage: true
    })=>{
        return (PageComponent)=>{
            const WithBloomreach = (pageProps)=>{
                const { pageModel , visitor , path , isMultilingualEnabled =false , locale =settings.defaultLocale , isContentPage ,  } = pageProps;
                const contextSettings = swcHelpers.objectSpread({}, settings.bloomreach, {
                    isMultilingualEnabled,
                    locale,
                    visitor,
                    pageModel,
                    path
                });
                const shouldWrap = !isContentPage && wrapInContainerPage;
                const Wrapper = shouldWrap ? CmsContainerPage : React__default.Fragment;
                return /*#__PURE__*/ React__default.createElement(BloomreachProvider, {
                    settings: contextSettings
                }, /*#__PURE__*/ React__default.createElement(Wrapper, null, /*#__PURE__*/ React__default.createElement(PageComponent, swcHelpers.extends({}, pageProps, {
                    isContentPage: isContentPage
                }))));
            };
            if (process.env.NODE_ENV !== 'production') {
                const displayName = PageComponent.displayName || PageComponent.name || 'Unknown';
                WithBloomreach.displayName = `WithBloomreach(${displayName})`;
            }
            WithBloomreach.getInitialProps = function() {
                var _ref = swcHelpers.asyncToGenerator(function*(ctx) {
                    var ref, ref1;
                    const { req , asPath ='' , res , pageModel  } = ctx;
                    const { defaultLocale , isMultilingualEnabled  } = settings;
                    const { apiUrl  } = settings.bloomreach;
                    // Somehow `query` is not populated on the context but it is on the request object,
                    // even though that shouldn't be possible according to TS
                    // @ts-ignore
                    const query = req === null || req === void 0 ? void 0 : req.query;
                    const preview = isPreview(query);
                    let locale = '';
                    if (isMultilingualEnabled) {
                        // LocaleUtils expects the default Request type from express but we can't access that here
                        locale = Boolean(req) ? LocaleUtils.getLocale(req, defaultLocale, preview) : defaultLocale;
                    }
                    // Get pageModel fetched on the server from the response object
                    // If it doesn't exist, we should try to fetch the brPage
                    // (in case of client-side request or server failure)
                    let rehydratedPageModel = (res === null || res === void 0 ? void 0 : (ref = res.locals) === null || ref === void 0 ? void 0 : ref.pageModel) || pageModel;
                    let { visitor  } = ctx;
                    if (!rehydratedPageModel) {
                        const brPage = yield BloomreachApi.getBrPage(apiUrl, asPath, {
                            locale,
                            headers: req === null || req === void 0 ? void 0 : req.headers,
                            connection: req === null || req === void 0 ? void 0 : req.connection,
                            isMultilingual: isMultilingualEnabled
                        });
                        rehydratedPageModel = parsePageModel(brPage);
                        if (!visitor) {
                            const v = brPage === null || brPage === void 0 ? void 0 : brPage.getVisitor();
                            if (v) {
                                visitor = v;
                            }
                        }
                    }
                    const isContentPage = ((ref1 = rehydratedPageModel === null || rehydratedPageModel === void 0 ? void 0 : rehydratedPageModel.page.models) === null || ref1 === void 0 ? void 0 : ref1.pageType) === 'content-page';
                    ctx.pageModel = rehydratedPageModel;
                    ctx.visitor = visitor;
                    ctx.isContentPage = isContentPage;
                    ctx.query = query;
                    let pageProps = {};
                    if (PageComponent.getInitialProps) {
                        pageProps = swcHelpers.objectSpread({
                            isContentPage
                        }, (yield PageComponent.getInitialProps(ctx)));
                    }
                    if (!rehydratedPageModel) {
                        console.error(`WithBloomreach: Failed to fetch page for ${asPath}`);
                    }
                    return swcHelpers.objectSpread({}, pageProps, {
                        visitor,
                        locale,
                        path: asPath,
                        isMultilingualEnabled,
                        pageModel: rehydratedPageModel
                    });
                });
                return function(ctx) {
                    return _ref.apply(this, arguments);
                };
            }();
            return WithBloomreach;
        };
    };
}
function isPreview(query) {
    return query && typeof query.token === 'string';
}

export { withBloomreach };
//# sourceMappingURL=with-bloomreach.js.map
